import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, TextField, Button, Alert, Chip, Snackbar, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BankSelect from '../common/bankSelect';
import SaveIcon from '@mui/icons-material/Save';
import {
    getMyReferrers, updateMyReferrer, archiveMyReferrer, getReferrerSales,
    uploadReferrerIcPhoto, getReferrerIcPhoto, deleteReferrerIcPhoto,
} from '../../api/referralLinkApi';
import { handlePriceWithRM } from '../../utils/priceUtils';
import { convertDateTimeToDateString } from '../../utils/datetimeUtils';

/**
 * One referrer, full width — the same shape as the admin user page: labelled sections,
 * two columns, Save at the bottom.
 * (user 2026-10-21: "when edit referral it will go to similar design like that full width"
 *  / "team and user and referral design must be same")
 *
 * Deliberately NOT the admin user page itself. That one needs the UserManagement right,
 * which a sales person does not have, and it exposes roles, commission rates, upline and
 * manager — none of which belong to the person who merely introduced somebody. This is
 * the same layout over the fields they may actually change.
 *
 * Loaded through the caller-scoped list rather than a by-id endpoint, so a referrer who
 * is not theirs cannot be reached by typing a different number into the address bar.
 */

const PP = "'Poppins', sans-serif";

const cardSx = {
    p: { xs: 2, md: 2.5 }, borderRadius: '14px', border: '1px solid #E2E8F0',
    backgroundColor: '#fff',
};
const headSx = {
    fontFamily: PP, fontSize: '0.72rem', fontWeight: 700, color: '#64748B',
    textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5,
};
const gridSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 };

function ReadOnly({ label, value }) {
    return (
        <Box>
            <Typography sx={{ fontFamily: PP, fontSize: '0.68rem', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                {label}
            </Typography>
            <Typography sx={{ fontFamily: PP, fontSize: '0.9rem', color: '#0F172A', fontWeight: 600 }}>
                {value}
            </Typography>
        </Box>
    );
}

export default function ReferrerEditPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [row, setRow] = useState(null);
    const [form, setForm] = useState({
        firstName: '', lastName: '', phone: '', icNumber: '',
        bankName: '', bankAccountNumber: '', bankBeneficiaryName: '',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [sales, setSales] = useState([]);

    /* The IC photocopy. Held as an object URL rather than an <img src="/uploads/...">
       because it is NOT a public file: the endpoint checks who is asking, so the
       bytes arrive as a blob and are wrapped locally. Revoked on unmount — an object
       URL that is never revoked keeps the whole image in memory for the session. */
    const [icUrl, setIcUrl] = useState('');
    const [icKind, setIcKind] = useState('');      // 'pdf' | 'image' | ''
    const [icBusy, setIcBusy] = useState(false);
    const [icError, setIcError] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        getMyReferrers()
            .then(res => {
                const found = (Array.isArray(res?.data) ? res.data : [])
                    .find(r => String(r.userId) === String(id));
                if (!found) { setError('That referrer is not one you introduced.'); return; }
                setRow(found);
                setForm({
                    firstName: found.firstName || '',
                    lastName: found.lastName || '',
                    phone: found.phoneNumber || '',
                    icNumber: found.icNumber || '',
                    bankName: found.bankName || '',
                    bankAccountNumber: found.bankAccountNumber || '',
                    bankBeneficiaryName: found.bankBeneficiaryName || '',
                });
            })
            .catch(() => setError('Could not load that referrer.'))
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => { load(); }, [load]);

    /* The sales behind the count. Loaded separately from the referrer so a failure
       here leaves the editable half of the page working — somebody filling in a bank
       account should not be blocked because a history could not be read. */
    /* Fetch whatever IC copy is on file. A 404 is the normal "none yet" answer and
       must not look like a failure. */
    const loadIc = useCallback(() => {
        let revoked = false;
        getReferrerIcPhoto(id)
            .then(res => {
                if (revoked) return;
                const blob = res?.data;
                if (!blob || !blob.size) return;
                setIcKind(blob.type === 'application/pdf' ? 'pdf' : 'image');
                setIcUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
            })
            .catch(() => { /* none on file */ });
        return () => { revoked = true; };
    }, [id]);

    useEffect(() => { loadIc(); }, [loadIc]);

    // Hand the object URL back when leaving the page.
    useEffect(() => () => { if (icUrl) URL.revokeObjectURL(icUrl); }, [icUrl]);

    const pickIc = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';                 // so the same file can be re-picked
        if (!file) return;
        setIcBusy(true);
        setIcError('');
        try {
            await uploadReferrerIcPhoto(id, file);
            loadIc();
        } catch (err) {
            const d = err?.response?.data;
            setIcError((typeof d === 'string' && d.trim()) || d?.message || 'Could not upload that.');
        } finally {
            setIcBusy(false);
        }
    };

    const removeIc = async () => {
        if (!window.confirm('Remove the IC copy for this referrer?')) return;
        setIcBusy(true);
        setIcError('');
        try {
            await deleteReferrerIcPhoto(id);
            setIcUrl(prev => { if (prev) URL.revokeObjectURL(prev); return ''; });
            setIcKind('');
        } catch {
            setIcError('Could not remove that.');
        } finally {
            setIcBusy(false);
        }
    };

    useEffect(() => {
        let alive = true;
        getReferrerSales(id)
            .then(res => { if (alive) setSales(Array.isArray(res?.data) ? res.data : []); })
            .catch(() => { /* the count on the card still stands */ });
        return () => { alive = false; };
    }, [id]);

    const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            await updateMyReferrer({ userId: Number(id), ...form });
            setSaved(true);
            load();
        } catch (err) {
            const d = err?.response?.data;
            setError(d?.message || (typeof d === 'string' && d.trim()) || 'Could not save that.');
        } finally {
            setSaving(false);
        }
    };

    const toggleArchive = async () => {
        const archiving = row?.status === 'Active';
        const name = `${form.lastName} ${form.firstName}`.trim();
        if (!window.confirm(`${archiving ? 'Archive' : 'Restore'} ${name}?`)) return;
        try {
            await archiveMyReferrer({ userId: Number(id), archive: archiving });
            load();
        } catch {
            setError('Could not change that.');
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    if (!row) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Alert severity="error" sx={{ fontFamily: PP }}>{error || 'Not found.'}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/team/referrals')}
                    sx={{ fontFamily: PP, textTransform: 'none', mt: 2 }}>
                    Back to Referral
                </Button>
            </Box>
        );
    }

    const archived = row.status !== 'Active';

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/team/referrals')}
                sx={{ fontFamily: PP, textTransform: 'none', color: '#64748B', mb: 1 }}>
                Referral
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.35rem', color: '#0F172A' }}>
                    {`${form.lastName} ${form.firstName}`.trim() || row.username}
                </Typography>
                <Chip size="small" label={archived ? 'Archived' : 'Active'}
                    sx={{
                        fontFamily: PP, fontWeight: 700, fontSize: '0.68rem',
                        backgroundColor: archived ? '#F1F5F9' : '#D1FAE5',
                        color: archived ? '#64748B' : '#065F46',
                    }} />
                {row.needsBankDetails && (
                    <Chip size="small" label="No bank account" color="warning"
                        sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.68rem' }} />
                )}
            </Box>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={headSx}>Personal info</Typography>
                        <Box sx={gridSx}>
                            <TextField size="small" label="First Name (Given Name)" value={form.firstName}
                                onChange={set('firstName')} fullWidth />
                            <TextField size="small" label="Last Name (Surname)" value={form.lastName}
                                onChange={set('lastName')} fullWidth />
                            <TextField size="small" label="Phone Number" value={form.phone}
                                onChange={set('phone')} fullWidth inputProps={{ inputMode: 'tel' }} />
                            <TextField size="small" label="IC Number" value={form.icNumber}
                                onChange={set('icNumber')} fullWidth />
                        </Box>
                    </Paper>

                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={headSx}>IC copy</Typography>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#94A3B8', mb: 1.5 }}>
                            A photo or scan of their IC, for the payout record. Only people who
                            can see this referrer can open it — it is not a public link.
                        </Typography>

                        {icError ? <Alert severity="error" sx={{ mb: 1.5 }}>{icError}</Alert> : null}

                        {icUrl ? (
                            <Box sx={{ mb: 1.5 }}>
                                {icKind === 'pdf' ? (
                                    <Button variant="outlined" size="small" href={icUrl} target="_blank"
                                        rel="noreferrer"
                                        sx={{ fontFamily: PP, textTransform: 'none' }}>
                                        Open the PDF
                                    </Button>
                                ) : (
                                    <Box component="a" href={icUrl} target="_blank" rel="noreferrer"
                                        sx={{ display: 'inline-block' }}>
                                        <Box component="img" src={icUrl} alt="IC copy"
                                            sx={{
                                                maxWidth: '100%', maxHeight: 220, borderRadius: '10px',
                                                border: '1px solid #E2E8F0', display: 'block',
                                            }} />
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#64748B', mb: 1.5 }}>
                                Nothing on file yet.
                            </Typography>
                        )}

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button variant="outlined" size="small" component="label" disabled={icBusy}
                                sx={{ fontFamily: PP, textTransform: 'none' }}>
                                {icBusy ? 'Working…' : icUrl ? 'Replace' : 'Upload IC copy'}
                                <input hidden type="file" accept="image/*,application/pdf" onChange={pickIc} />
                            </Button>
                            {icUrl && (
                                <Button size="small" color="error" onClick={removeIc} disabled={icBusy}
                                    sx={{ fontFamily: PP, textTransform: 'none' }}>
                                    Remove
                                </Button>
                            )}
                        </Box>
                    </Paper>

                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={headSx}>Billing &amp; bank</Typography>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#94A3B8', mb: 1.5 }}>
                            Where a referral fee is paid. Without an account number nothing can be paid out.
                        </Typography>
                        <Box sx={gridSx}>
                            <BankSelect label="Bank Name" value={form.bankName}
                                onChange={(v) => setForm(prev => ({ ...prev, bankName: v }))} />
                            <TextField size="small" label="Bank Beneficiary Name" value={form.bankBeneficiaryName}
                                onChange={set('bankBeneficiaryName')} fullWidth
                                placeholder="Exactly as it appears on the account" />
                            <TextField size="small" label="Bank Account No" value={form.bankAccountNumber}
                                onChange={set('bankAccountNumber')} fullWidth />
                        </Box>
                    </Paper>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={headSx}>Referral</Typography>
                        <Box sx={{ ...gridSx, rowGap: 2 }}>
                            <ReadOnly label="Ref ID" value={row.userId} />
                            <ReadOnly label="Username" value={row.username} />
                            <ReadOnly label="Rate" value={row.rate != null ? `${Number(row.rate)}%` : '—'} />
                            <ReadOnly label="Added" value={row.createdOn ? convertDateTimeToDateString(row.createdOn) : '—'} />
                        </Box>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#94A3B8', mt: 1.5 }}>
                            The rate is fixed for referrers and is not changed from here.
                        </Typography>
                    </Paper>

                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={headSx}>Sales history</Typography>
                        <Box sx={{ ...gridSx, mb: sales.length ? 2 : 0 }}>
                            <ReadOnly label="Sales" value={row.salesCount ?? 0} />
                            <ReadOnly label="Value" value={handlePriceWithRM(Number(row.salesValue) || 0)} />
                        </Box>

                        {sales.length === 0 ? (
                            <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#64748B', mt: 1.5 }}>
                                Nothing yet. A sale appears here once one is created with this
                                person as the referrer.
                            </Typography>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {['Customer', 'Agent', 'Date', 'Status', 'Value'].map(h => (
                                                <TableCell key={h} sx={{
                                                    fontFamily: PP, fontWeight: 600, fontSize: '0.68rem',
                                                    color: '#64748B', textTransform: 'uppercase',
                                                    letterSpacing: '0.4px', whiteSpace: 'nowrap',
                                                    borderBottom: '2px solid #E2E8F0', px: 1, py: 0.75,
                                                }}>
                                                    {h}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sales.map(sale => (
                                            <TableRow key={sale.salesId} hover
                                                // /accounting/sales/{id} is where a sale opens —
                                                // the route every other list uses. /sales/{id}
                                                // does not exist.
                                                onClick={() => navigate(`/accounting/sales/${sale.salesId}`)}
                                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#F0F7FF' } }}>
                                                <TableCell sx={{ fontFamily: PP, fontSize: '0.8rem', px: 1, py: 0.75 }}>
                                                    {sale.customerName || '—'}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: PP, fontSize: '0.8rem', px: 1, py: 0.75 }}>
                                                    {sale.agentName || '—'}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: PP, fontSize: '0.8rem', px: 1, py: 0.75, whiteSpace: 'nowrap' }}>
                                                    {sale.depositOn ? convertDateTimeToDateString(sale.depositOn) : '—'}
                                                </TableCell>
                                                <TableCell sx={{ px: 1, py: 0.75 }}>
                                                    <Chip size="small" label={sale.status || '—'}
                                                        sx={{ fontFamily: PP, fontSize: '0.64rem', height: 20 }} />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: PP, fontSize: '0.8rem', px: 1, py: 0.75, whiteSpace: 'nowrap' }}>
                                                    {handlePriceWithRM(Number(sale.finalPrice) || 0)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#94A3B8', mt: 1.5 }}>
                            Fees earned are in Reports → My Referral Report, which is the one place
                            a referral fee is worked out.
                        </Typography>
                    </Paper>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={() => navigate('/team/referrals')}
                    sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px' }}>
                    Cancel
                </Button>
                <Button variant="outlined" onClick={toggleArchive}
                    sx={{
                        fontFamily: PP, textTransform: 'none', borderRadius: '8px',
                        color: archived ? '#10B981' : '#64748B',
                        borderColor: archived ? '#10B981' : '#CBD5E1',
                    }}>
                    {archived ? 'Restore' : 'Archive'}
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={save}
                    sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px', fontWeight: 700 }}>
                    {saving ? 'Saving…' : 'Save Details'}
                </Button>
            </Box>

            <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)} message="Saved" />
        </Box>
    );
}
